import { AfterViewInit, Component, Input, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";

/**
 * Component to wrap the standard input field for text with customizable
 * error labels and values.
 */

@Component({
  selector: "fixed-label-text-input",
  templateUrl: "fixed-label-text-input.component.html",
})
export class FixedLabelTextInputComponent implements OnInit, AfterViewInit {
  // Configuration properties
  @Input() group: FormGroup;
  @Input() config;

  // Internal data model
  id: string;
  componentId: string;
  label: any;
  placeholder: string = "";
  maxLength: number = 300; //default
  minLength: number;
  required: boolean;
  disabled: boolean;
  readonly: boolean = false;
  mustEqual: false;
  showCharCounter: boolean = false;
  preventExceedMaxLength: boolean = false;
  autocorrect: string = "on";
  autocomplete: string = "on";
  autofocus: boolean = false;
  hidden: boolean = false;
  hideIfDisabled: boolean = false;

  constructor(public formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.id = this.config.id;
    this.componentId = this.config.componentId;
    this.label =
      this.config.label !== undefined ? this.config.label : this.config.id;
    if (this.label === "false") {
      this.label = false;
    }
    this.readonly = this.config.readonly;
    if (this.config.placeholder) {
      this.placeholder = this.config.placeholder;
    }
    if (this.config.autofocus) {
      this.autofocus = this.config.autofocus;
    }
    this.group.addControl(this.id, this.formBuilder.control(""));
    this.disabled = this.group.controls[this.id].disabled;
    if (this.hideIfDisabled) {
      this.hidden = this.disabled;
    }

    this.composeValidators();

    const selfControl = this.group.controls[this.id];
    selfControl.valueChanges.subscribe((data) => {
      if (this.disabled !== selfControl.disabled) {
        this.disabled = selfControl.disabled;
      }
      if (this.hideIfDisabled) {
        this.hidden = this.disabled;
      }
    });
  }

  ngAfterViewInit(): void {
    this.group.controls[this.id].setErrors(this.getErrors());

    // Errors can get lost when manually set
    // This is an overzealous approach to guarantee they get maintained
    this.group.valueChanges.subscribe((data) => {
      this.group.controls[this.id].setErrors(this.getErrors());
    });
  }

  private composeValidators() {
    const validators = [];
    if (this.config.rules) {
      if (this.config.rules.required) {
        this.required = this.config.rules.required;
        validators.push(Validators.required);
      }

      if (this.config.rules.minLength) {
        this.minLength = this.config.rules.minLength;
        validators.push(Validators.minLength(this.minLength));
      } else {
        if (this.config.rules.required) {
          validators.push(Validators.minLength(1));
        }
      }

      if (this.config.rules.maxLength) {
        this.maxLength = this.config.rules.maxLength;
        validators.push(Validators.maxLength(this.maxLength));
      }

      if (this.config.rules.mustEqual) {
        this.mustEqual = this.config.rules.mustEqual;
        validators.push(
          Validators.pattern(
            `(?:^|\\W)${this.config.rules.mustEqual.toString()}(?:$|\\W)`
          )
        );
      }

      if (this.config.rules.showCharCounter) {
        this.showCharCounter = this.config.rules.showCharCounter;
      }

      if (this.config.rules.preventExceedMaxLength) {
        this.preventExceedMaxLength = this.config.rules.preventExceedMaxLength;

        this.group.controls[this.id].valueChanges.subscribe((data) => {
          if (
            this.preventExceedMaxLength &&
            data &&
            data.length > this.maxLength
          ) {
            this.group.controls[this.id].setValue(
              data.substring(0, this.maxLength)
            );
          }
        });
      }

      if (this.config.rules.autocomplete) {
        this.autocomplete = this.config.rules.autocomplete;
      }
      if (this.config.rules.autocorrect) {
        this.autocorrect = this.config.rules.autocorrect;
      }
      if (this.config.rules.hideIfDisabled) {
        this.hideIfDisabled = this.config.rules.hideIfDisabled;
      }
    }

    const composedValidators: ValidatorFn = Validators.compose(validators);

    const selfControl = this.group.controls[this.id];

    selfControl.setValidators(composedValidators);
  }

  getErrors(): ValidationErrors {
    let errors = {};
    if (
      this.required &&
      (undefined === this.getCurrentValue() ||
        null === this.getCurrentValue() ||
        "" === this.getCurrentValue())
    ) {
      errors["required"] = true;
    }
    if (
      null !== this.getCurrentValue() &&
      "" !== this.getCurrentValue() &&
      undefined !== this.getCurrentValue()
    ) {
      if (this.getCurrentValue().length < this.minLength) {
        errors["minlength"] = true;
      }

      if (this.getCurrentValue().length > this.maxLength) {
        errors["maxlength"] = true;
      }

      if (this.mustEqual) {
        if (this.getCurrentValue() !== this.mustEqual) {
          errors["pattern"] = true;
        }
      }
    }

    return Object.keys(errors).length == 0 ? undefined : errors;
  }

  getCurrentValue(): string {
    let textValue = this.group.controls[this.id].value;
    return textValue;
  }

  checkChar(event: any) {
    if (this.config.rules) {
      let newValue = event.target.value;
      if (this.config.rules.alphaOnly) {
        let regExp = new RegExp("^[A-Za-z? ]+$");
        if (!regExp.test(newValue)) {
          event.target.value = newValue.replace(/[^A-Za-z ]/g, "");
        }
      } else if (this.config.rules.alphaNumericOnly) {
        let regExp = new RegExp("^[A-Za-z0-9? ]+$");
        if (!regExp.test(newValue)) {
          event.target.value = newValue.replace(/[^A-Za-z0-9 ]/g, "");
        }
      } else if (this.config.rules.replaceApostrophe) {
        event.target.value = newValue.replaceAll(/’/g, "'");
      }
    }
  }

  addValidationRules(ruleObject: any) {
    this.config.rules = Object.assign({}, this.config.rules, ruleObject);
    this.composeValidators();
  }

  formatData(phone: string, eRef) {
    if (this.config.rules) {
      if (phone) {
        if (this.config.rules.formatPhone) {
          let result = phone.replace(/[^0-9]+/g, "");
          if (result.length > 6) {
            result =
              result.slice(0, 3) +
              "-" +
              result.slice(3, 6) +
              "-" +
              result.slice(6);
          } else if (result.length > 3) {
            result = result.slice(0, 3) + "-" + result.slice(3);
          }
          eRef.value = result;
        } else if (this.config.rules.formatZipCode) {
          let result = phone.replace(/[^0-9]+/g, "");
          eRef.value = result;
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/inputs/fixed-label-text-input/fixed-label-text-input.component.ts